import { useLocation } from 'react-router-dom';
import { Button, Callout, Lockup } from '@beyond-js/ui/react';
import wordmark from '@brand/beyond-logo-wordmark.svg';
import { Session } from '@snapshots/domain';
import { usePresence } from '@snapshots/session';

/**
 * Nobody is signed in on arrival and the hand-off did not start by itself (D25): the person signed out,
 * a sign-in came back without a session (refused, or an authority that could not be reached), or a
 * departure within the last minute came back signed out; since D26 it also says whether the session the
 * browser carried expired or was ended at Accounts, and a suspended account gets no way in at all,
 * because signing in again would not help. A session that ends while the person works never comes here:
 * it is the family's `Session` (`useRenewal`). The page says which, and its
 * action goes through the service's hand-off to Beyond Accounts, back to the address the person
 * opened (its path and query), even within the loop guard.
 */
export function SignIn() {
	const { text, session, entry, ending } = usePresence();
	const { pathname, search } = useLocation();
	const failure = Session.failure(pathname, search);
	// D26: a suspended account is told so, with no way in, because signing in again would not help
	if (ending === 'suspended' && !failure) {
		return (
			<div className="mx-auto flex max-w-md flex-col gap-4 px-4 py-16" data-ending="suspended">
				<div className="self-start [--bui-lockup-height:24px]">
					<Lockup src={wordmark} name={text('app.product')} />
				</div>
				<h1 className="text-title-1 font-semibold">{text('suspended.title')}</h1>
				<p className="text-body">{text('suspended.lead')}</p>
			</div>
		);
	}
	const reason = failure ? (failure.code === 'UPSTREAM_UNAVAILABLE' ? 'signin.unavailable' : 'signin.failed') : ending && `signin.${ending}`;
	return (
		<div className="mx-auto flex max-w-md flex-col gap-4 px-4 py-16">
			{/* The family lockup at its own width, on the heading's edge, not stretched across the column */}
			<div className="self-start [--bui-lockup-height:24px]">
				<Lockup src={wordmark} name={text('app.product')} />
			</div>
			<h1 className="text-title-1 font-semibold">{text('signin.title')}</h1>
			{reason && (
				<div data-ending={failure ? `failed:${failure.code}` : ending}>
					<Callout tone={failure || ending === 'unconfirmed' || ending === 'incomplete' || ending === 'revoked' ? 'warning' : 'info'} live title={text(reason)} />
				</div>
			)}
			<p className="text-small text-ink-muted">{text('signin.body')}</p>
			<div>
				<Button variant="primary" href={session.signin(Session.destination(pathname, search))} onClick={() => entry.depart()} label={text('action.signin')} data-signin />
			</div>
		</div>
	);
}
